import dayjs from 'dayjs';

/**
 * generateRoundRobin
 * @param arrayOfTeams
 * @param arrayOfPlaceholders
 * @param playedMatches
 * @param roundRobinFormat
 */

export const generateRoundRobin = (arrayOfTeams: any, arrayOfPlaceholders: any, playedMatches: any, roundRobinFormat: 'single' | 'double' | 'triple') => {
	// If no array of teams then return
	if (!arrayOfTeams) return;

	// Ensure we only have unique teams
	const teams = [...new Set([...arrayOfTeams, ...arrayOfPlaceholders])];

	if (teams.length % 2 !== 0) {
		console.warn('Cannot perform round robin with uneven teams');
		console.log(teams);
		return { schedule: [], teams };
	}

	// Convert roundRobinFormat to integer
	let rrFormat: number;
	switch (roundRobinFormat) {
		case 'double':
			rrFormat = 2;
			break;
		case 'triple':
			rrFormat = 3;
			break;
		default:
			rrFormat = 1;
	}

	// const nTeams = teams.length;
	// const nOpponents = nTeams - 1;
	// const gamesPerRound = teams.length / 2;
	// const gamesPerRobin = nOpponents * gamesPerRound;

	// Split list of teams into two arrays
	const halves = splitTeams(teams);

	// Make a copy of the halves
	let halvesCache = [...halves];
	let rounds = [];

	// Calculate number of rotations based on round robin type
	const nRotations = (teams.length - 1) * rrFormat;

	// Generate rounds
	for (let i = 0; i < nRotations; i++) {
		const _matches = genMatches(halvesCache, i);
		rounds.push(_matches);
		halvesCache = rotate(halvesCache, teams);
	}

	// Flatten round into one single array
	const allMatches = rounds.reduce(function(acc, cur) {
		return acc.concat(cur);
	}, []);

	// console.log(playedMatches.length, 'have already been scheduled');
	// console.log(allMatches.length, 'matches to generate in total for this league');

	// console.log('playedMatches');
	// console.log(playedMatches);

	// Filter out played matches
	const filteredMatches = allMatches.filter((m1: { homeTeam: string; awayTeam: string }) => {
		if (!m1.homeTeam || !m1.awayTeam) throw new Error();
		let played = false;
		playedMatches.map((m2: { homeTeam: string; awayTeam: string }) => {
			if (!m2.homeTeam || !m2.awayTeam) throw new Error();
			const sameHomeTeam = m1.homeTeam === m2.homeTeam;
			const sameAwayTeam = m1.awayTeam === m2.awayTeam;
			if (sameHomeTeam && sameAwayTeam) played = true;
			if (rrFormat === 1) {
				if (m1.homeTeam === m2.awayTeam && m1.awayTeam === m2.homeTeam) played = true;
			}
		});
		return !played;
	});

	// console.log(filteredMatches.length, 'matches left after removing those already scheduled');

	if (filteredMatches.length === 0) {
		console.warn('No more matches to generate');
		return { schedule: [], teams };
	}

	let matches = [...filteredMatches];
	let nShortestRounds;
	let best;

	// Keep on shuffling array to find the schedule with the least number of rounds
	for (let n = 0; n < 1000; n++) {
		const s1 = generate(matches);
		s1.sort((a: any, b: any) => {
			return b.length - a.length;
		});
		if (!nShortestRounds || nShortestRounds > s1.length) {
			nShortestRounds = s1.length;
			best = s1;
		}
		matches = shuffle(matches);
	}

	return { schedule: best, teams };
};

const splitTeams = (teams: any[]) => {
	return teams.reduce(
		(acc: any[], cur: any, index: number) => {
			const n = index + 1;
			if (n % 2) acc[0].push(cur);
			else acc[1].push(cur);
			return acc;
		},
		[[], []]
	);
};

// Take the two lists and make a round of matches
const genMatches = (halves: any, i: number) => {
	const homeHalf = i % 2 ? halves[0] : halves[1];
	const awayHalf = i % 2 ? halves[1] : halves[0];
	return homeHalf.map((t: any, index: number) => {
		return {
			homeTeam: t,
			awayTeam: awayHalf[index],
		};
	});
};

// Rotate the teams in the lists
const rotate = (halves: any, teams: any[]) => {
	const _halves = [...halves];
	// If only two teams in the league then skip rotation
	if (teams.length === 2) {
		return _halves;
	}
	const home = _halves[0].splice(1, 1);
	const away = _halves[1].pop();
	_halves[0].push(away);
	_halves[1].unshift(...home);
	return _halves;
};

// Shuffle array
const shuffle = (array: any) => {
	var currentIndex = array.length,
		temporaryValue,
		randomIndex;

	// While there remains elements to shuffle...
	while (0 !== currentIndex) {
		// Pick a remaining element...
		randomIndex = Math.floor(Math.random() * currentIndex);
		currentIndex -= 1;

		// And swap it with the current element.
		temporaryValue = array[currentIndex];
		array[currentIndex] = array[randomIndex];
		array[randomIndex] = temporaryValue;
	}

	return array;
};

// Generate an array of unique matches
const generate = (array: any) => {
	// Loop through each match in array
	return array.reduce((acc: any[], cur: any) => {
		// if this is the first item then create a new group and push into accumulator
		if (!acc.length) acc.push([cur]);
		else {
			let spaceFound;
			// Loop through each group in the accumulator and search for one which does not already contain this team
			for (let groupIndex = 0; groupIndex < acc.length; groupIndex++) {
				const group = acc[groupIndex];

				// Does team already exist in this group?
				const itemExists = group.find((match: any) => {
					const existingTeams = [match.homeTeam, match.awayTeam];
					return existingTeams.includes(cur.homeTeam) || existingTeams.includes(cur.awayTeam);
				});

				// If team does not exist in this group then push to this group and stop searching
				if (!itemExists) {
					group.push(cur);
					spaceFound = true;
					break;
				} else spaceFound = false;
			}

			// If no space is found in an existing group then create new group
			if (spaceFound === false) acc.push([cur]);
		}
		return acc;
	}, []);
};

/**
 * buildGroupTables
 * Converts one or more groups of matches into league tables
 * @param groups A 2d array with the inner arrays being lists of matches
 */

export const buildGroupTables = (groups: any[], carriedPoints: any, isFinalsGroup: boolean = false) => {
	return groups.map((group: any) => {
		const matches = group.matches;
		const table = matches.reduce((acc: any, cur: any) => {
			// Loop through each match and return if match has not been played
			const result = cur.home_score !== null || cur.away_score !== null;
			// if (!result) return acc;

			const teamIds = new Set();
			const teams = [] as any[];
			console.log('checking here', matches);
			matches.map((m: any) => {
				let found = false;
				if (m.deleted_at) return false;
				if (m.placeholder_home_team && m.phHomeTeamObj.type === 'bye') return false;
				if (m.placeholder_away_team && m.phAwayTeamObj.type === 'bye') return false;
				if (m.placeholder_home_team && m.phHomeTeamObj.type === 'BYE') return false;
				if (m.placeholder_away_team && m.phAwayTeamObj.type === 'BYE') return false;
				// If a home placeholder team and not already in the array
				if (m.placeholder_home_team && !teamIds.has(m.placeholder_home_team)) {
					if (m.phHomeTeamObj) teams.push(m.phHomeTeamObj);
					if (m.phHomeTeam) teams.push(m.phHomeTeam);
					found = true;
				}

				// If an away placeholder team and not already in the array
				if (m.placeholder_away_team && !teamIds.has(m.placeholder_away_team)) {
					if (m.phAwayTeamObj) teams.push(m.phAwayTeamObj);
					if (m.phAwayTeam) teams.push(m.phAwayTeam);
					found = true;
				}

				// If a home team and not already in the array
				if (m.home_team && !teamIds.has(m.home_team)) {
					if (isFinalsGroup) {
						if (m.deleted_team !== m.home_team) {
							if (m.homeTeamObj) teams.push(m.homeTeamObj);
							if (m.homeTeam) teams.push(m.homeTeam);
							found = true;
						}
					} else {
						if (m.homeTeamObj) teams.push(m.homeTeamObj);
						if (m.homeTeam) teams.push(m.homeTeam);
						found = true;
					}
				}

				// If an away team and not already in the array
				if (m.away_team && !teamIds.has(m.away_team)) {
					if (isFinalsGroup) {
						if (m.deleted_team !== m.away_team) {
							if (m.awayTeamObj) teams.push(m.awayTeamObj);
							if (m.awayTeam) teams.push(m.awayTeam);
							found = true;
						}
					} else {
						if (m.awayTeamObj) teams.push(m.awayTeamObj);
						if (m.awayTeam) teams.push(m.awayTeam);
						found = true;
					}
				}

				if (m.placeholder_home_team) teamIds.add(m.placeholder_home_team);
				if (m.placeholder_away_team) teamIds.add(m.placeholder_away_team);
				if (m.home_team) teamIds.add(m.home_team);
				if (m.away_team) teamIds.add(m.away_team);
			});

			// Find home and away team
			const homeTeamObj = teams.find((team: any) => {
				if (cur.home_team) return team.id === cur.home_team;
				if (cur.placeholder_home_team) return team.id === cur.placeholder_home_team;
			});
			const awayTeamObj = teams.find((team: any) => {
				if (cur.away_team) return team.id === cur.away_team;
				if (cur.placeholder_away_team) return team.id === cur.placeholder_away_team;
			});

			// if (!homeTeamObj) {
			// 	throw new Error('no homeTeamObj');
			// }

			// if (!awayTeamObj) {
			// 	throw new Error('no awayTeamObj');
			// }

			const homeTeam = homeTeamObj && homeTeamObj.name;
			const awayTeam = awayTeamObj && awayTeamObj.name;
			const homeTeamId = homeTeamObj && homeTeamObj.id;
			const awayTeamId = awayTeamObj && awayTeamObj.id;

			// Check if a key already exists in accumulator for either team
			const homeTeamExists = acc[homeTeam];
			const awayTeamExists = acc[awayTeam];

			// Check result of match
			const homeWin = cur && cur.home_score > cur.away_score;
			const homeLoss = cur && cur.home_score < cur.away_score;
			const draw = cur && cur.home_score === cur.away_score;

			// If no object key exists for the homeTeam, create one
			if (!homeTeamExists) {
				if (result) {
					acc[homeTeam] = {
						name: homeTeam,
						teamId: homeTeamId,
						played: 1,
						won: cur.home_score > cur.away_score ? 1 : 0,
						drawn: cur.home_score === cur.away_score ? 1 : 0,
						lost: cur.home_score < cur.away_score ? 1 : 0,
						goalsScored: cur.home_score || 0,
						goalDiff: cur.home_score - cur.away_score,
						points: homeWin ? 3 : draw ? 1 : 0,
					};
				} else {
					acc[homeTeam] = {
						name: homeTeam,
						teamId: homeTeamId,
						played: 0,
						won: 0,
						drawn: 0,
						lost: 0,
						goalsScored: 0,
						goalDiff: 0,
						points: 0,
					};
				}
			}

			// If no object key exists for the awayTeam, create one
			if (!awayTeamExists) {
				if (result) {
					acc[awayTeam] = {
						name: awayTeam,
						teamId: awayTeamId,
						played: 1,
						won: cur.away_score > cur.home_score ? 1 : 0,
						drawn: cur.away_score === cur.home_score ? 1 : 0,
						lost: cur.away_score < cur.home_score ? 1 : 0,
						goalsScored: cur.away_score || 0,
						goalDiff: cur.away_score - cur.home_score,
						points: !homeWin && !draw ? 3 : draw ? 1 : 0,
					};
				} else {
					acc[awayTeam] = {
						name: awayTeam,
						teamId: awayTeamId,
						played: 0,
						won: 0,
						drawn: 0,
						lost: 0,
						goalsScored: 0,
						goalDiff: 0,
						points: 0,
					};
				}
			}

			// If home team already exists
			if (result && homeTeamExists) {
				// Add 1 to home teams played games
				acc[homeTeam].played++;
				// If home team won add 1 to total wins
				if (homeWin) acc[homeTeam].won++;
				// If draw add 1 to total drawn
				if (draw) acc[homeTeam].drawn++;
				// If home team lost add 1 to total lost
				if (homeLoss) acc[homeTeam].lost++;
				// Add home_score to home teams total goals scored
				acc[homeTeam].goalsScored = acc[homeTeam].goalsScored + cur.home_score;
				// Add goalDiff from match to total goalDiff
				acc[homeTeam].goalDiff = acc[homeTeam].goalDiff + cur.home_score - cur.away_score;
				// If home win add 3 points to total points
				if (homeWin) acc[homeTeam].points = acc[homeTeam].points + 3;
				// If draw add 1 point to total points
				if (draw) acc[homeTeam].points++;
			}

			// If away team already exists (as above, in reverse)
			if (result && awayTeamExists) {
				acc[awayTeam].played++;
				if (homeLoss) acc[awayTeam].won++;
				if (draw) acc[awayTeam].drawn++;
				if (homeWin) acc[awayTeam].lost++;
				acc[awayTeam].goalsScored = acc[awayTeam].goalsScored + cur.away_score;
				acc[awayTeam].goalDiff = acc[awayTeam].goalDiff + cur.away_score - cur.home_score;
				if (homeLoss) acc[awayTeam].points = acc[awayTeam].points + 3;
				if (draw) acc[awayTeam].points++;
			}

			return acc;
		}, {});

		let arr = Object.keys(table).map(function(key) {
			return table[key];
		});

		// Apply carried points
		if (carriedPoints) {
			arr = arr.map((t: any) => {
				if (!carriedPoints[t.teamId]) {
					return {
						...t,
						points: t.points || 0,
						played: 0,
						won: 0,
						drawn: 0,
						lost: 0,
						goalDiff: 0,
						goalsScored: 0,
					};
				} else {
					const { drawn, goalDiff, goalScored, lost, played, points, won } = carriedPoints[t.teamId];
					return {
						...t,
						points: t.points + points,
						played: t.played + played || 0,
						won: t.won + won || 0,
						drawn: t.drawn + drawn || 0,
						lost: t.lost + lost || 0,
						goalDiff: t.goalDiff + goalDiff || 0,
						goalsScored: t.goalsScored + goalScored || 0,
					};
				}
			});
		}

		// FIXME: Find a less hacky way of remove the BYE team
		const finalArray = arr
			.filter((team: any) => {
				return team.name && team.name !== 'BYE';
			})
			.sort((a: any, b: any) => {
				if (a.points === b.points && a.goalDiff === b.goalDiff) return b.goalsScored - a.goalsScored;
				if (a.points === b.points) return b.goalDiff - a.goalDiff;
				else return b.points - a.points;
			})
			.map((team: any) => {
				return {
					...team,
					status: group.status,
				};
			});

		return finalArray;
	});
};

/**
 * combineDuplicateMatches
 * @param matches
 */

export const combineDuplicateMatches = (matches: any[]) => {
	// Check for any duplicate matches and if they exist then combine them into one match
	const matchIds = new Set();
	return matches.reduce((acc: any, cur: any) => {
		// If this match has already been updated
		if (matchIds.has(cur.id)) {
			// Get match from acc
			const matchIndex = acc.findIndex((m: any) => m.id === cur.id);
			const exisitingUpdatedMatch = { ...acc[matchIndex] };
			// Create updated match
			const updatedMatch = {
				id: cur.id,
				home_team: cur.home_team ? cur.home_team : exisitingUpdatedMatch.home_team,
				away_team: cur.away_team ? cur.away_team : exisitingUpdatedMatch.away_team,
				placeholder_home_team: exisitingUpdatedMatch.home_team ? null : cur.placeholder_home_team,
				placeholder_away_team: exisitingUpdatedMatch.away_team ? null : cur.placeholder_away_team,
			};
			acc[matchIndex] = updatedMatch;
		} else {
			acc.push(cur);
		}
		matchIds.add(cur.id);
		return acc;
	}, []);
};

/**
 * shuffleTeams
 * Shuffles any array
 * @param array
 */
export const shuffleTeams = (array: any[]) => {
	var currentIndex = array.length,
		temporaryValue,
		randomIndex;

	// While there remain elements to shuffle...
	while (0 !== currentIndex) {
		// Pick a remaining element...
		randomIndex = Math.floor(Math.random() * currentIndex);
		currentIndex -= 1;

		// And swap it with the current element.
		temporaryValue = array[currentIndex];
		array[currentIndex] = array[randomIndex];
		array[randomIndex] = temporaryValue;
	}

	return array;
};

/**
 * truncateString
 * Returns a truncated version of the string given to it
 * @param str String to be truncated
 * @param n Length of returned string
 */
export const truncateString = (str: string, n: number) => {
	if (str.length <= n) return str;
	return `${str.substring(0, n)} ...`;
};

export const calculateFirstKnockoutMatchTimeAndDate = (details: any) => {
	console.log('calculateFirstKnockoutMatchTimeAndDate');

	// Convert rround robin format string into int
	let rrN = 0;
	switch (details.rrFormat) {
		case 'single':
			rrN = 1;
			break;
		case 'double':
			rrN = 2;
			break;
		case 'triple':
			rrN = 3;
			break;
	}

	let nMatches = 0;
	// Loop over each group
	details.groups.map((nTeams: any) => {
		// Calculate number of matches in each group
		const _nMatches = nTeams * (nTeams - 1);
		// Add to total and multiply by rr amount
		nMatches = nMatches + _nMatches * rrN;
	});

	const perPitch = Math.ceil(nMatches / details.groupPitches);

	const minutes = details.groupSlotDuration * perPitch;

	// Add this number of minutes onto the kick off time for the group stages
	const firstMatch = `${details.lastMatchKickOffDate} ${details.lastMatchKickOffTime}`;
	const dayjsObj = dayjs(new Date(firstMatch));

	const nextMatch = dayjsObj.add(minutes, 'minute');
	return {
		date: nextMatch.format('YYYY/MM/DD'),
		time: nextMatch.format('HH:mm'),
	};
};

export const validateMatchTeams = (matches: any[]) => {
	// Check for invalid matches
	const errors = [...matches].filter((m: any) => {
		if (m.home_team && m.placeholder_home_team) return true;
		if (!m.home_team && !m.placeholder_home_team) return true;
		if (m.away_team && m.placeholder_away_team) return true;
		if (!m.away_team && !m.placeholder_away_team) return true;
		return false;
	});

	if (errors.length) {
		console.log('invalid matches');
		console.log(errors);
		return false;
	} else return true;
};

export const getBasicPlaceholders = (matches: any[]) => {
	const ids = new Set();
	const placeholders = [] as any[];
	matches.map((m: any) => {
		if (m.phHomeTeamObj && m.phHomeTeamObj.type === 'basic' && !ids.has(m.phHomeTeamObj.id)) {
			placeholders.push(m.phHomeTeamObj);
			ids.add(m.phHomeTeamObj.id);
		}
		if (m.phAwayTeamObj && m.phAwayTeamObj.type === 'basic' && !ids.has(m.phAwayTeamObj.id)) {
			placeholders.push(m.phAwayTeamObj);
			ids.add(m.phAwayTeamObj.id);
		}
	});
	return placeholders;
};

export const getRealTeams = (matches: any[]) => {
	const ids = new Set();
	const teams = [] as any[];
	matches.map((m: any) => {
		if (m.homeTeamObj && m.homeTeamObj.type === 'team' && !ids.has(m.homeTeamObj.id)) {
			teams.push(m.homeTeamObj);
			ids.add(m.homeTeamObj.id);
		}
		if (m.awayTeamObj && m.awayTeamObj.type === 'team' && !ids.has(m.awayTeamObj.id)) {
			teams.push(m.awayTeamObj);
			ids.add(m.awayTeamObj.id);
		}
	});
	return teams;
};

export const getSeedingPlaceholders = (matches: any[]) => {
	console.log('getSeedingPlaceholders');
	const ids = new Set();
	const placeholders = [] as any[];
	matches.map((m: any) => {
		if (m.phHomeTeamObj && m.phHomeTeamObj.type === 'seed' && !ids.has(m.phHomeTeamObj.id)) {
			placeholders.push(m.phHomeTeamObj);
			ids.add(m.phHomeTeamObj.id);
		}
		if (m.phAwayTeamObj && m.phAwayTeamObj.type === 'seed' && !ids.has(m.phAwayTeamObj.id)) {
			placeholders.push(m.phAwayTeamObj);
			ids.add(m.phAwayTeamObj.id);
		}
	});
	return placeholders;
};

export const getNextMatch = (matches: any[], type: string) => {
	const filteredMatches = matches.filter((m: any) => {
		if (type === 'fixture' && m.home_score == null && m.away_score == null) return true;
		if (type === 'result' && m.home_score !== null && m.away_score !== null) return true;
	});
	return filteredMatches.reduce((acc: any, cur: any) => {
		if (acc === undefined || !acc) return;
		if (!acc.date) {
			acc = cur;
			return acc;
		}
		if (acc === undefined) return;
		if (cur === undefined) return;
		if (!acc || !cur || acc.date === undefined || cur.date === undefined || !acc.date || !cur.date) return;
		const aDate = parseInt(acc.date.replaceAll('-', ''));
		const cDate = parseInt(cur.date.replaceAll('-', ''));
		if (cDate < aDate) acc = cur;
		return acc;
	}, {});
};

export const convertMatchTitle = (m: any) => {
	if (m.league) return m.leagueObj.display_name;
	if (m.finals_group) return m.finalsGroupObj.final.display_name;
	if (m.finals_knockout) return m.finalsKnockoutObj.final.display_name;
	if (m.is_friendly) return 'Friendly';
	else {
		console.log(m);
		return 'convertMatchTitle: Fix me';
	}
};

export const convertMatchName = (m: any) => {
	const stage = m.name ? m.name.split('-')[0] : null;
	if (stage) {
		switch (stage) {
			case 'F':
				return 'Final';
			case 'SF':
				return 'Semi Final';
			case 'QF':
				return 'Quarter Final';
			case 'L16':
				return 'Last 16';
			case 'L32':
				return 'Last 32';
			case 'PF':
				return '3/4 Finals';
		}
	}
	if (m.finals_group) return 'Group Stage Match';
	if (m.league) {
		if (m.leagueObj.league_type === 'mini-league') return 'Mini League Match';
		else return 'League Match';
	}
	if (m.is_friendly) return 'Friendly Match';
	else {
		console.log('unable to convert match name');
		console.log(m);
		return null;
	}
};

export const convertToSnakeCase = (string: string) => {
	const regex = /(-|\s)/gim;
	return string.replace(regex, '_');
};

export const convertToTimestamp = (date: string, time: string) => {
	return new Date(`${date} ${time}`).getTime();
};
export const filteredMatches = (matches: any) => {
	let futureMatchesWithResults: any = [];
	const currentDate = new Date();
	matches.map((match: any) => {
		const matchDate = new Date(match.date + ' ' + match.kick_off_time);
		if (match.home_score !== null && match.away_score !== null && currentDate < matchDate) {
			futureMatchesWithResults.push(match);
		}
	});
	const filteredMatches = matches.filter((match: any) => {
		return !futureMatchesWithResults.find((m: any) => {
			return match.id === m.id;
		});
	});
	return filteredMatches;
};

export const assignCalendarColor = (match: any, matchTypes: any[]) => {
	const classNames = ['.cal-bg-red', '.cal-bg-blue', '.cal-bg-green', '.cal-bg-purple', '.cal-bg-orange', '.cal-bg-gray'];
	return;
};

export const computeLeagueStatus = (l: any) => {
	const kickOffDateTz = new Date(l.kick_off_date).getTime() - 86400000;
	const now = new Date().getTime();
	if (l.published && kickOffDateTz < now && l.status !== 'finished') return 'active';
	if (kickOffDateTz > now || (!l.published && l.status !== 'finished')) return 'planned';
	if (l.status === 'finished' && l.published) return 'finished';
	return 'archived';
};

export const computeLeagueResultsNeeded = (l: any): { resultsNeeded: boolean; outstanding?: any } => {
	const now = new Date().getTime() - 86400000;
	const { matches, status, published } = l;
	if (status === 'finished' || published === false) return { resultsNeeded: false };
	const fixtures = matches.filter((m: any) => m.away_score === null && m.home_score === null);
	let outstanding = fixtures.filter((m: any) => {
		const kickOffDateTz = new Date(m.date).getTime();
		return kickOffDateTz < now;
	});
	outstanding = outstanding.filter((outstanding: any) => outstanding.date);
	outstanding = outstanding.filter((outstanding: any) => {
		const kickOffDateTz = new Date(outstanding.date).getTime();
		return kickOffDateTz < now;
	});
	return { resultsNeeded: outstanding.length };
};

export const computeFinalsStatus = (f: any) => {
	const kickOffDateTz = new Date(f.kick_off_date).getTime() - 86400000;
	const now = new Date().getTime();
	if (f.published && kickOffDateTz < now && f.status !== 'finished') return 'active';
	if (kickOffDateTz > now || (!f.published && f.status !== 'finished')) return 'planned';
	if (f.status === 'finished' && f.published) return 'finished';
	return 'archived';
};

// TODO: Fix this. At the moment matches are not pulled in with finals
export const computeFinalsResultsNeeded = (f: any) => {
	const now = new Date().getTime() - 86400000;
	const { finals_groups, finals_knockouts, status, published } = f;

	// If finished or not published return
	if (status === 'finished' || published === false) return false;

	//Check finals group
	let finals_groups_matches: any[] = [];
	for (let i = 0; i < finals_groups.length; i++) {
		let filtered = finals_groups[i].matches.filter((m: any) => m.away_score === null && m.home_score === null);
		for (let i = 0; i < filtered.length; i++) {
			finals_groups_matches = [...finals_groups_matches, filtered[i]];
		}
	}
	let g_matches = finals_groups_matches.filter((outstanding: any) => {
		const kickOffDateTz = new Date(outstanding.date).getTime();
		return kickOffDateTz < now;
	});
	//return if found such matches
	if (g_matches.length) {
		return true;
	}

	//Check finals knockouts
	let finals_knockouts_matches: any[] = [];
	for (let i = 0; i < finals_knockouts.length; i++) {
		let filtered2 = finals_knockouts[i].matches.filter((m: any) => m.away_score === null && m.home_score === null);
		for (let i = 0; i < filtered2.length; i++) {
			finals_knockouts_matches = [...finals_knockouts_matches, filtered2[i]];
		}
	}
	let k_matches = finals_knockouts_matches.filter((outstanding: any) => {
		const kickOffDateTz = new Date(outstanding.date).getTime();
		return kickOffDateTz < now;
	});
	//return if found such matches
	if (k_matches.length) {
		return true;
	}
	return false;
	// const now = new Date().getTime();
	// const { status, published } = f;
	// const matches: any[] = [];
	// if (status === 'finished' || published === false) return false;
	// const fixtures = matches.filter((m: any) => m.away_score === null && m.home_score === null);
	// const outstanding = fixtures.filter((m: any) => {
	// 	const kickOffDateTz = new Date(m.date).getTime();
	// 	return kickOffDateTz < now;
	// });
	// return outstanding.length > 0;
};
